
import React, { forwardRef, useState, useMemo, useCallback, useRef } from 'react';
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import { FieldTypes } from 'utils/formUtils';
import message from "components/CustomMessage";
import GenericMappingForm from '../GenericMappingForm';
import _ from 'utils/lodashUtils';
import useAPIResponse from 'hooks/useAPIResponse';
import GlobalSettingAPI from 'restAPI/GlobalSettingAPI';
import { compact, isUndefined, map, split } from 'lodash';
import { Form } from 'antd';

const FORM_DEFAULT_VALUES = [
  {
    resourceTags: [
      { key: '', value: '' }
    ],
  }
];

const RedlockMappingForm = forwardRef(function RedlockMappingForm({disableProjectFields = false, ...props }, ref) {
  const [isEnabledRedLockTagsBasedMatching] = useAPIResponse(() => GlobalSettingAPI.getRedLockTagsBasedMatchingBoolean(), [])
  const [projects, setProjects] = useState(() => {
    if (props.record) {
      const { cloudId, cloudName, cloudType } = props.record;
      if(cloudId){
        const cloudIdArray = cloudId.split(',');
        const cloudNameArray = cloudName.split(',');
        const cloudTypeArray = cloudType.split(",")
        return cloudIdArray.map((id, index) => ({id, name: cloudNameArray[index],  otherProperties: { type: cloudTypeArray[index] }}));
      }
    }
    return [];
  });
  const [form] = Form.useForm();
  const apiParamsRef = useRef({});
  const selectedAccountGroupNames = useRef([]);

  const fetchAccountGroups = useCallback(() => {
     let { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: props.loginConfigId, type: "accountGroup" });
      promise = promise.then((response) => {
        const { projects } = response;
        return projects;
      }).catch(()=>{
        message.error('Problem fetching Account groups')
      });
      return { promise, cancel };
  }, [props.loginConfigId, props.toolName])

  const fetchCloudAccounts = useCallback((params) => {
      let { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: props.loginConfigId, ...params });
      promise = promise.then((response) => {
        const { projects } = response;
        setProjects(projects);
        return projects;
      }).catch(()=>{
        message.error('Problem fetching Cloud accounts')
      });
      return { promise, cancel };
  }, [props.loginConfigId, props.toolName]);

  const getExtraFieldsData = (config, index) => {
    const accountGroupName = selectedAccountGroupNames.current[index];

    const { cloudName, resourceTags, accountGroupId } = config;

    let tags = map(compact(resourceTags), (i) => {
      return _.isEmpty(i["key"]) || _.isEmpty(i["value"])
        ? `${i["key"] || ""}${i["value"] || ""}`
        : `${i["key"]}:${i["value"]}`;
    }).join();
    if (_.isEmpty(tags)) {
      tags = undefined;
    }

    let fieldsData = {
      accountGroupId,
      accountGroupName,
      resourceTags: tags,
      ...(config.discovery_config_id && {
        discovery_config_id: config.discovery_config_id,
      }),
    }

    if(!cloudName){
      fieldsData.cloudName = null;
      fieldsData.cloudId = null;
    }
    else {
      const data = projects.reduce(
        (acc, cv) => {
          const { id, name, otherProperties } = cv;
          if(cloudName.includes(id) || cloudName.includes(name)){
            acc.id.push(id);
            acc.name.push(name);
            acc.type.push(otherProperties.type);
          }
          return acc;
        },
        { id: [], name: [], type: [] }
      );

      fieldsData = {
        ...fieldsData,
        componentDisplayName: data.name.join(),
        cloudName: data.name.join(),
        cloudId: data.id.join(),
        cloudType: data.type.join(),
      };
    }

    return fieldsData
  };

  const parseExtraFieldsDataFromResponse = useCallback((config) => {
    const { accountGroupId, accountGroupName, cloudId } = config
    selectedAccountGroupNames.current[0] = accountGroupName;
    
    const data = {
      accountGroupId,
      cloudName: cloudId ? split(cloudId, ",") : undefined,
      ...(config.discovery_config_id && { discovery_config_id: config.discovery_config_id }),
    }
    if(config.resourceTags){
      const resourceTags = config.resourceTags.split(",");
      const tags = resourceTags.map(t => ({
        "key": t.split(":")[0],
        "value": t.split(":")[1]
      }));
      data.resourceTags = tags;
    }
    return data;
  }, [])

  const onAccountGroupChange = useCallback((value, field, selectedOption) => {
    const { name } = field;
    selectedAccountGroupNames.current[field.name] = selectedOption.name;
    const configurations = form.getFieldValue("configurations") || [];
    let updatedConfiguration = [...configurations];
    updatedConfiguration[name] = {
      ...updatedConfiguration[name],
      cloudName: undefined,
    };
    form.setFieldsValue({ configurations: updatedConfiguration });
  }, [form]);

  const extraFields = useMemo(() => ([
      {
      name: 'accountGroupId',
      colSpan: '10',
      placeholder: 'Account Group',
      type: FieldTypes.SELECT,
      label: 'Account Group',
      required: !(props.record && !props.record.accountGroupId),
      disabled: disableProjectFields,
      props: {
        autoFocus: !disableProjectFields,
        onChange: onAccountGroupChange,
        api: fetchAccountGroups,
      }
    },
    {
      name: 'cloudName',
      colSpan: '10',
      placeholder: 'Cloud account',
      type: FieldTypes.MULTI_SELECT,
      label: 'Cloud account',
      disabled: disableProjectFields,
      props: {
        setExtraProps: (field, listField) => {
          const allConfigs = form.getFieldsValue().configurations;
          const accountGroupID = allConfigs[listField.name]?.accountGroupId;
          if (!apiParamsRef.current[accountGroupID] || (props.record && isUndefined(apiParamsRef.current[accountGroupID].project))) {
            const accountGroupName = selectedAccountGroupNames.current[listField.name];
            const isEditModeWithNoAccountGroup = props.record && apiParamsRef.current[accountGroupID]
            apiParamsRef.current[accountGroupID] = { name: isEditModeWithNoAccountGroup ? "NA" : accountGroupName,  project: isEditModeWithNoAccountGroup ? "NA" : accountGroupID };
          }
          return {
            api: props.record ? fetchCloudAccounts : accountGroupID && fetchCloudAccounts,
            apiParams: apiParamsRef.current[accountGroupID],
            resetOptionsProp: accountGroupID,
            mode: "multiple",
            allowClear: true,
          }
        }
      }
    },
    ...(isEnabledRedLockTagsBasedMatching ? [{
      name: (listField) => ([listField.name, "resourceTags"]),
      label: "Tags",
      type: FieldTypes.LIST,
      listClass: 'list-collapse',
      props: {
        displayIcon: true,
        minimumRow: 1,
      },
      fieldRows: [
        [{
          name: "key",
          label: "Key",
          colSpan: 6,
          disabled: disableProjectFields,
        },{
          name: "value",
          label: "Value",
          colSpan: 6,
          disabled: disableProjectFields,
        }
        ]
      ]

    }] : [])
  ]),
   [disableProjectFields, onAccountGroupChange, fetchAccountGroups, isEnabledRedLockTagsBasedMatching, form, props.record, fetchCloudAccounts],
  );

  return (
    <GenericMappingForm
      ref={ref}
      form={form}
      formDefaultValues={FORM_DEFAULT_VALUES}
      projects={projects}
      getExtraFieldsData={getExtraFieldsData}
      parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
      extraFields={extraFields}
      {...props}
    />
  )
})

export default RedlockMappingForm;
